import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { platformApi, PlatformApiError } from '../lib/platformApi';

/**
 * ═══ ONBOARDING A SCHOOL ONTO WHATSAPP, WITHOUT ANYBODY REPEATING THE FIRST ATTEMPT ═══
 *
 * ⚠ THIS PAGE EXISTS BECAUSE THE KNOWLEDGE WAS ONLY IN ONE PERSON'S HEAD, AND IT COST WEEKS.
 * Every warning below is a thing that actually went wrong: a number that could never be used again,
 * a display name that would have been refused, a portfolio restriction that blocked every route at
 * once, and four days spent waiting on the wrong verification. None of it is discoverable from
 * Meta's own screens at the moment you need it.
 *
 * ⚠ IT IS A CHECKLIST AND A READ MODEL, NOT A WIZARD THAT CLICKS THROUGH META. The steps that
 * happen on Meta's or a provider's site cannot be automated from here; what this can do is say
 * exactly what to do, in order, warn before the irreversible ones, and then show where each school
 * has actually got to.
 */

type Stage = 'todo' | 'doing' | 'done' | 'blocked';

interface SchoolWhatsApp {
  tenantId: string;
  tenantName: string;
  /** 'CloudApi' | 'Gupshup' — a string, not a union: a provider the server adds later must render, not crash. */
  provider: string;
  state: string;
  canSend: boolean;
  displayName: string | null;
  mobile: string | null;
  /** Plain-language to-do list from SenderIdentity.WhatIsLeft(). */
  whatIsLeft: string[];
  blockedReason: string | null;
  templatesApproved: number;
  templatesTotal: number;
  /** 'Unknown' | 'Green' | 'Yellow' | 'Red'. */
  quality: string;
}

export default function WhatsAppOnboardingPanel() {
  const [rows, setRows] = useState<SchoolWhatsApp[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    platformApi.get<SchoolWhatsApp[]>('/api/platform/whatsapp/onboarding')
      .then(setRows)
      .catch((e) => setErr(e instanceof PlatformApiError ? e.message : 'Could not load school status.'));
  }, []);

  return (
    <div style={{ display: 'grid', gap: 22 }}>
      <Guide />
      <Status rows={rows} err={err} />
    </div>
  );
}

/* ─────────────────────────── the part that stops it happening again ─────────────────────────── */

function Guide() {
  return (
    <div className="card" style={{ padding: 22 }}>
      <h2 style={{ margin: '0 0 4px', fontSize: 20 }}>Connecting a school's WhatsApp</h2>
      <p style={{ margin: '0 0 18px', color: 'var(--ink-2)', fontSize: 13.5, lineHeight: 1.7 }}>
        Read the whole page once before starting a school. Two of these steps cannot be undone, and
        both of them look harmless at the time.
      </p>

      <Step n={1} title="The school owns its WhatsApp, not us">
        <p>
          The WhatsApp Business Account, the number and the display name belong to the school. A
          parent sees <b>Excellent CBSE School</b>, never AIVENTRIX.
        </p>
        <Warn>
          Never put more than one school on one number. WhatsApp scores quality per <i>number</i>, so
          one school's careless campaign would throttle every other school's absence alerts — and a
          school cannot be asked to accept that its safeguarding messages depend on a stranger's
          marketing.
        </Warn>
      </Step>

      <Step n={2} title="Get a number that is not on WhatsApp">
        <p>
          The school buys or frees a number for this. Any prepaid SIM works; it never needs to sit in
          a phone after verification, because the API does not use the handset. Voice and SMS keep
          working normally on it.
        </p>
        <Warn>
          A number put on the API can <b>never be used in the ordinary WhatsApp app again</b>. Do not
          use the number printed on somebody's visiting card, and do not use the office number staff
          chat from. This is the single most expensive mistake on this page.
        </Warn>
        <Warn>
          If the number has <i>ever</i> had WhatsApp on it, delete that account from inside the app
          first — Settings → Account → Delete my account. Uninstalling is not enough, and the
          leftover registration fails verification with an error that does not mention it.
        </Warn>
      </Step>

      <Step n={3} title="Check the school's own Meta standing before anything else">
        <p>
          The school signs in with <i>their</i> Facebook account and uses <i>their</i> business
          portfolio. Ours is not involved, and a restriction on ours does not block them.
        </p>
        <Warn>
          If their portfolio has any restriction, stop and resolve that first. A restricted portfolio
          blocks WhatsApp account creation, blocks adding a Page, and blocks the verification that
          would fix it — every route at once, with error messages that each blame something else.
        </Warn>
        <Note>
          Business Verification is not the same as "Verify yourself or an organisation". The second
          is advertiser identity verification for ad transparency and does nothing for WhatsApp.
          Four days were lost to that confusion once already.
        </Note>
      </Step>

      <Step n={4} title="Pick the provider">
        <p>
          <b>Gupshup</b> — works today, the provider carries it, costs a margin.{' '}
          <b>Cloud API</b> — direct to Meta, no margin, needs our own Tech Provider standing.
        </p>
        <Note>
          It is a per-school setting. A school started on a provider stays there until it has a
          reason to move; moving a live account is disruptive and there is never a good week for it.
        </Note>
      </Step>

      <Step n={5} title="Display name — the field that gets refused">
        <p>The school's real name, matching whatever documents they verify with.</p>
        <Warn>
          No taglines, no "Official", no emoji, no city, and never a product name. Meta checks it
          against the registration documents and a refusal costs days, not minutes.
        </Warn>
      </Step>

      <Step n={6} title="Register the templates, then wait">
        <p>
          Nothing can be sent to a parent who has not messaged first except an approved template.
          Fee receipt, absence alert, gate release, exam schedule, report card ready — each one
          submitted per language.
        </p>
        <Warn>
          A template approved in English is <b>not</b> approved in Kannada. They are separate
          submissions with separate outcomes, and Eduvora refuses to send one it has no approval for.
          Submit both at the same time or the Kannada-medium parents get nothing.
        </Warn>
        <Warn>
          Watch the category Meta grants, not the one we asked for. A fee reminder submitted as
          Utility and approved as Marketing costs several times more, needs marketing consent, and is
          scrubbed against Do Not Call. The school's row below shows it when they differ.
        </Warn>
      </Step>

      <Step n={7} title="Send to yourself first, then one class">
        <p>
          Check how the display name renders on a real phone before any parent sees it. Then one
          class, not the whole roll.
        </p>
        <Warn>
          A new number may only start about 250 conversations a day. A 600-parent fee run on day one
          half-fails, and nothing on any screen tells the office which half — so they cannot follow
          up and cannot re-send without doubling up on the first 250.
        </Warn>
      </Step>

      <Step n={8} title="Never do these" tone="danger">
        <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.9 }}>
          <li>Create a second business portfolio to get around a restriction on the first. Verification uses the same documents, so Meta links them anyway — and a portfolio created to escape enforcement is a worse offence than the enforcement.</li>
          <li>Use an unofficial WhatsApp bridge. It violates Meta's terms, the ban is permanent and unappealable, and it lands on the school's number.</li>
          <li>Send to a list the school bought. It is illegal without consent under DPDP and the blocks count against the school's number, not the seller's.</li>
        </ul>
      </Step>
    </div>
  );
}

/* ────────────────────────────────── where each school has got to ────────────────────────────── */

function Status({ rows, err }: { rows: SchoolWhatsApp[] | null; err: string | null }) {
  return (
    <div className="card" style={{ padding: 22 }}>
      <h3 style={{ margin: '0 0 14px', fontSize: 16 }}>Where each school has got to</h3>

      {err && <p style={{ color: 'var(--danger, #b4232a)', fontSize: 13 }}>{err}</p>}
      {!err && rows === null && <p style={{ color: 'var(--ink-2)', fontSize: 13 }}>Loading…</p>}
      {!err && rows?.length === 0 && (
        <p style={{ color: 'var(--ink-2)', fontSize: 13 }}>No school has started connecting WhatsApp yet.</p>
      )}

      {rows?.map((r) => <SchoolRow key={r.tenantId} r={r} />)}
    </div>
  );
}

function SchoolRow({ r }: { r: SchoolWhatsApp }) {
  const stage: Stage = r.blockedReason ? 'blocked'
    : r.canSend && r.templatesApproved > 0 ? 'done'
    : r.canSend || r.whatIsLeft.length > 0 ? 'doing'
    : 'todo';

  return (
    <div style={{ borderTop: '1px solid var(--line)', padding: '14px 0' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <b style={{ fontSize: 14.5 }}>{r.tenantName}</b>
        <Pill stage={stage} />
        <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>{r.provider}</span>
        {/* ⚠ The number is shown because onboarding goes wrong on exactly this field, and a person
            checking a school needs to see WHICH number was used without opening another screen. */}
        {r.mobile && <span style={{ fontSize: 12, color: 'var(--ink-2)' }}>{r.mobile}</span>}
        <QualityPill quality={r.quality} />
      </div>

      {r.displayName && (
        <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 4 }}>
          Parents see: <b>{r.displayName}</b>
        </div>
      )}

      {r.blockedReason && (
        <p style={{ margin: '8px 0 0', fontSize: 13, color: 'var(--danger, #b4232a)' }}>{r.blockedReason}</p>
      )}

      {r.whatIsLeft.length > 0 && (
        <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 13, lineHeight: 1.75, color: 'var(--ink-2)' }}>
          {r.whatIsLeft.map((w, i) => <li key={i}>{w}</li>)}
        </ul>
      )}

      <div style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 6 }}>
        Templates approved: <b>{r.templatesApproved}</b> of {r.templatesTotal}
        {r.templatesApproved < r.templatesTotal && r.canSend && (
          // ⚠ SAID OUT LOUD, because it is the failure that looks like a bug. The number works, the
          // school is "connected", and messages still refuse — because the template is not approved.
          <span> — anything without an approved template will refuse to send.</span>
        )}
      </div>
    </div>
  );
}

function Pill({ stage }: { stage: Stage }) {
  const map: Record<Stage, { t: string; bg: string; fg: string }> = {
    todo: { t: 'Not started', bg: 'var(--surface)', fg: 'var(--ink-2)' },
    doing: { t: 'In progress', bg: '#fff4e0', fg: '#8a5a00' },
    done: { t: 'Sending', bg: '#e6f6ec', fg: '#1a6b39' },
    blocked: { t: 'Blocked', bg: '#fdeaea', fg: '#b4232a' },
  };
  const s = map[stage];
  return <span style={{ background: s.bg, color: s.fg, fontSize: 11.5, fontWeight: 650, padding: '2px 8px', borderRadius: 999 }}>{s.t}</span>;
}

/**
 * ⚠ YELLOW IS SHOWN AS A WARNING, NOT AS "FINE". A rating is a lagging measure: by the time it is
 * Red, parents have already blocked the number and the school has already lost the ability to reach
 * them about fees. Yellow is the last moment at which stopping still helps.
 */
function QualityPill({ quality }: { quality: string }) {
  if (!quality || quality === 'Unknown') return null;
  const bad = quality === 'Yellow' || quality === 'Red';
  return (
    <span style={{
      background: bad ? '#fdeaea' : '#e6f6ec', color: bad ? '#b4232a' : '#1a6b39',
      fontSize: 11.5, fontWeight: 650, padding: '2px 8px', borderRadius: 999,
    }}>
      Quality {quality}{bad ? ' — stop campaigns' : ''}
    </span>
  );
}

/* ────────────────────────────────────────── bits ────────────────────────────────────────────── */

function Step({ n, title, children, tone }: { n: number; title: string; children: ReactNode; tone?: 'danger' }) {
  return (
    <section style={{ borderTop: '1px solid var(--line)', padding: '14px 0 2px' }}>
      <h4 style={{ margin: '0 0 6px', fontSize: 14.5, color: tone === 'danger' ? 'var(--danger, #b4232a)' : undefined }}>
        {tone === 'danger' ? '' : `${n}. `}{title}
      </h4>
      <div style={{ fontSize: 13.5, lineHeight: 1.75, color: 'var(--ink-2)', display: 'grid', gap: 8 }}>{children}</div>
    </section>
  );
}

function Warn({ children }: { children: ReactNode }) {
  return (
    <p style={{
      margin: 0, padding: '9px 12px', background: '#fdeaea', borderLeft: '3px solid #b4232a',
      color: '#7a1a20', borderRadius: 4,
    }}>{children}</p>
  );
}

function Note({ children }: { children: ReactNode }) {
  return (
    <p style={{
      margin: 0, padding: '9px 12px', background: 'var(--surface)', borderLeft: '3px solid var(--line)',
      borderRadius: 4,
    }}>{children}</p>
  );
}
